import { Collection } from "./collection";
import { bufferedPipe, Condition } from "./condition";
import { Semaphore } from "./lock";
import { Resource } from "./resource";

/**
 * Async iterable of promises
 */
export interface PromiseStream<T> extends AsyncIterable<() => Promise<T>> {}

/**
 * Return promise stream and yield results as they finish. An error halts
 * processing and is thrown. Successive errors are discarded.
 */
export async function* promiseStreamRun<T>(
  promises: PromiseStream<T>,
): AsyncIterableIterator<T> {
  const [write, read] = bufferedPipe<T>();

  let halt = false;
  const producer = (async () => {
    const pending = new Collection<Promise<void>>();
    try {
      for await (const fn of promises) {
        if (halt) {
          break;
        }
        const key = pending.add(
          fn()
            .then(
              async (value) => {
                if (halt) {
                  return;
                }
                write.next(value);
              },
              async (e) => {
                if (halt) {
                  return;
                }
                halt = true;
                write.throw(e);
              },
            )
            .finally(() => {
              pending.remove(key);
            }),
        );
      }
    } finally {
      await Promise.allSettled(pending);
      write.end();
    }
  })();

  try {
    yield* read;
  } finally {
    // if interrupted, stop the producer
    halt = true;
    await producer;
  }
}

/** Run concurrently, but yield only the completed prefix in input order. */
export async function* promiseStreamRunOrdered<T>(
  promises: PromiseStream<T>,
): AsyncIterableIterator<T> {
  const indexed = (async function* () {
    let index = 0;
    for await (const fn of promises) {
      const current = index++;
      yield async () => ({ index: current, value: await fn() });
    }
  })();
  const completed = new Map<number, { value: T }>();
  let next = 0;
  for await (const result of promiseStreamRun(indexed)) {
    completed.set(result.index, result);
    for (;;) {
      const entry = completed.get(next);
      if (!entry) {
        break;
      }
      completed.delete(next++);
      yield entry.value;
    }
  }
}

/**
 * Limits concurrency of a promise stream
 */
export async function* promiseStreamThrottle<T>(
  promises: PromiseStream<T>,
  max: number,
): PromiseStream<T> {
  const semaphore = new Semaphore(max);
  await semaphore.acquire();
  for await (const fn of promises) {
    yield () => fn().finally(() => semaphore.release());
    await semaphore.acquire();
  }
}

// @ts-expect-error unused-export-debt
interface ResourcePromiseStream<R, T> extends AsyncIterable<
  (value: R) => Promise<T>
> {}

export async function* resourcePromiseStreamRun<R, T>(
  resource: Resource<R>,
  stream: AsyncIterable<(value: R) => Promise<T>>,
  signal?: AbortSignal,
): AsyncIterableIterator<T> {
  const end = Symbol();
  type Result = { type: "value"; value: T } | { type: "error"; error: unknown };
  const [write, read] = bufferedPipe<Promise<Result> | typeof end>();

  let aborted = signal?.aborted ?? false;
  const onAbort = () => {
    aborted = true;
    write.end();
  };
  signal?.addEventListener("abort", onAbort);

  (async (): Promise<typeof end> => {
    try {
      if (aborted) {
        return end;
      }
      for await (const item of stream) {
        if (aborted) {
          break;
        }
        const proceed = new Condition<void>();
        const result = resource(async (value) => {
          proceed.resolve();
          return await item(value);
        }).then<Result, Result>(
          (value) => ({ type: "value", value }),
          (error) => {
            // Settle every failed item before queuing it for a stopped consumer.
            aborted = true;
            proceed.resolve();
            return { type: "error", error };
          },
        );
        write.next(result);
        await proceed.result;
      }
      return end;
    } finally {
      signal?.removeEventListener("abort", onAbort);
    }
  })().then(
    (v) => {
      if (!aborted) {
        write.next(v);
      } else {
        write.end();
      }
    },
    (e) => {
      if (!aborted) {
        write.next(Promise.resolve({ type: "error", error: e }));
      } else {
        write.end();
      }
    },
  );

  for await (const resultPromise of read) {
    if (resultPromise === end) {
      break;
    }
    const result = await resultPromise;
    if (result.type === "error") {
      throw result.error;
    }
    yield result.value;
  }
}
